import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** API */
import ProductFormatsApi from 'api/setting/default-products/product-format.api'

/** UTILS || SLICE */
import { submitModal } from 'app/slice/modal.slice'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import HeaderCard from 'component/CardCustom/HeaderCard'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import MainButton from 'new-components/buttons/main-button'
import SwitchButton from 'new-components/buttons/switch-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import PageModal from 'new-components/modal/page-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

const initStateErrorMessage = {
  PRODUCT_FORMAT_NAME: ``,
  IS_DUPLICATE_PRODUCT_FORMAT_NAME: ``,
  PRODUCT_FORMAT_NAME_STRING_BASE: ``,
  PRODUCT_FORMAT_NAME_STRING_EMPTY: ``,
  PRODUCT_FORMAT_NAME_MAXIMUM_LENGTH: ``,
  PRODUCT_FORMAT_NAME_ANY_REQUIRED: ``,

  PRODUCT_FORMAT_NAME_EN: ``,
  IS_DUPLICATE_PRODUCT_FORMAT_NAME_EN: ``,
  PRODUCT_FORMAT_NAME_EN_STRING_BASE: ``,
  PRODUCT_FORMAT_NAME_EN_STRING_EMPTY: ``,
  PRODUCT_FORMAT_NAME_EN_MAXIMUM_LENGTH: ``,
  PRODUCT_FORMAT_NAME_EN_ANY_REQUIRED: ``
}
export default function MedicineFormats() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [productFormats, setProductFormats] = useState([])

  const [productFormatId, setProductFormatId] = useState(Number)
  const [productFormatName, setProductFormatName] = useState('')
  const [productFormatNameEn, setProductFormatNameEn] = useState('')
  const [productFormatStatus, setProductFormatStatus] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')
  const [disabledSubmit, setDisabledSubmit] = useState(false)
  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const [isOpenPageModal, setIsOpenPageModal] = useState(false)


  const loadData = async () => {
    try {
      const resp = await ProductFormatsApi.findAll({
        group: 'MEDICINE',
        page: page,
        pageLimit: pageLimit,
        search: search,
        sortBy: sortBy,
        sortType: sortType,
        status: status === 'ALL' ? '' : status
      })
      if (resp.status === 200) {
        setRowCount(resp.headers['x-total'])
        setProductFormats(resp.data)
      } else {
        setProductFormats([])
      }
    } catch (error) {
      notiError('ERROR')
    }
  }

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])

  const submit = () => {
    if (!productFormatName) return setErrorMessage({ ...errorMessage, ...{ PRODUCT_FORMAT_NAME: t('MEDICINE_FORMAT.MESSAGE.PRODUCT_FORMAT_NAME') } })

    dispatch(submitModal())
    setDisabledSubmit(true)
    ProductFormatsApi.create({ productFormatName, productFormatNameEn, group: 'MEDICINE' })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t('MEDICINE_FORMAT.MESSAGE.SUCCESS.CREATE'), '', null, null)
          resetForm()
          loadData()
          setIsOpenPageModal(false)
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`MEDICINE_FORMAT.MESSAGE.${err.message}`) } })
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`MEDICINE_FORMAT.MESSAGE.${err.message}`) } })
      }).finally(() => setDisabledSubmit(false))
  }

  const submitEdit = () => {
    if (!productFormatName) return setErrorMessage({ ...errorMessage, ...{ PRODUCT_FORMAT_NAME: t(`MEDICINE_FORMAT.MESSAGE.PRODUCT_FORMAT_NAME`) } })

    dispatch(submitModal())
    setDisabledSubmit(true)
    ProductFormatsApi.update(productFormatId, { productFormatName, productFormatNameEn, status: productFormatStatus, group: 'MEDICINE' })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`MEDICINE_FORMAT.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          setIsOpenPageModal(false)
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`MEDICINE_FORMAT.MESSAGE.${err.message}`) } })
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`MEDICINE_FORMAT.MESSAGE.${err.message}`) } })
      }).finally(() => setDisabledSubmit(false))
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setProductFormatId(0)
    setProductFormatName('')
    setProductFormatNameEn('')
    setProductFormatStatus('')
    clearErrorMessage()
    setIsOpenPageModal(false)
  }

  const onActive = () => {
    if (id) {
      ProductFormatsApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('MEDICINE_FORMAT.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('MEDICINE_FORMAT.MESSAGE.ERROR'))
        }
      })
    }
  }
  const onDelete = () => {
    if (id) {
      ProductFormatsApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('MEDICINE_FORMAT.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('MEDICINE_FORMAT.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    const { productFormatId: pdFormatId, productFormatName: pdFormatName, productFormatNameEn: pdFormatNameEn, status: pdFormatStatus } = data
    setProductFormatId(pdFormatId)
    setProductFormatName(pdFormatName)
    setProductFormatNameEn(pdFormatNameEn)
    setProductFormatStatus(pdFormatStatus)
    setIsOpenPageModal(true)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }
  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }
  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'productFormatName', disablePadding: false, label: t('MEDICINE_FORMAT.TABLE.CELL.PRODUCT_FORMAT_NAME') },
    { id: 'productFormatNameEn', disablePadding: false, label: t('MEDICINE_FORMAT.TABLE.CELL.PRODUCT_FORMAT_NAME_EN') },
    { id: 'status', disablePadding: false, label: t('MEDICINE_FORMAT.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('MEDICINE_FORMAT.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { productFormatId: pdFormatId, productFormatName: pdFormatName, productFormatNameEn: pdFormatNameEn, updatedBy, updatedAt, status: pdFormatStatus } = objData
    const statusBtnActive = pdFormatStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = pdFormatStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled

    const objRenderData = {
      key: pdFormatId,
      id: pdFormatId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: pdFormatName },
        { option: 'TEXT', align: 'left', label: pdFormatNameEn },
        { option: 'STATUS', align: 'center', label: pdFormatStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.productFormatName)
    setNameEn(obj.productFormatNameEn)
  }

  return (
    <Box>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`MEDICINE_FORMAT.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`MEDICINE_FORMAT.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { productFormatName: name, productFormatNameEn: nameEn && `(${nameEn})`, status: t(`MEDICINE_FORMAT.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <HeaderCard text={t('MEDICINE_FORMAT.TITLE')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('MEDICINE_FORMAT.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                { value: 'INACTIVE', name: t('STATUS.INACTIVE') }
              ]}
              label={t('STATUS.LABEL')}
              value={status}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} xl={8}>
            <Box display={'flex'} justifyContent={'space-between'}>
              <MainButton
                title={t('INPUT.SEARCH')}
                type='primary'
                variant='contained'
                style={{ width: '120px' }}
                onClick={() => {
                  setPage(1)
                  loadData()
                }} />

              <MainButton
                title={t('MEDICINE_FORMAT.BUTTON.ADD')}
                disabled={permissions.isCreate.disabled}
                type='primary'
                variant='contained'
                onClick={() => setIsOpenPageModal(true)}
                startIcon={<AddCircleIcon />}
              />
            </Box>
          </Grid>
        </Grid>
        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={productFormats.map((val, i) => {
              return renderData(val, i)
            })}
          />
        </Box>
        <PageModal
          isOpen={isOpenPageModal}
          title={productFormatId ? t('MEDICINE_FORMAT.TITLE_UPDATE') : t('MEDICINE_FORMAT.TITLE_CREATE')}
          size='XS'
          handleClose={resetForm}
          onCancel={{ onClick: resetForm, title: t('MEDICINE_FORMAT.BUTTON.CANCEL') }}
          onSubmit={{ onClick: productFormatId ? submitEdit : submit, title: t('MEDICINE_FORMAT.BUTTON.SAVE'), disabled: disabledSubmit }}
        >
          <Box display="flex" flexDirection="column" gap="24px">
            {productFormatId ? <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography>{t('INPUT.LABEL_STATUS')}</Typography>
              <SwitchButton onChange={() => setProductFormatStatus(productFormatStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')} checked={productFormatStatus === 'ACTIVE' ? true : false} />
            </Box> : ''}
            <TextInput
              onChange={(event) => {
                setProductFormatName(event.target.value)
                clearErrorMessage()
              }}
              value={productFormatName}
              label={t('MEDICINE_FORMAT.INPUT.PRODUCT_FORMAT_NAME')}
              helperText={
                errorMessage.PRODUCT_FORMAT_NAME ||
                errorMessage.IS_DUPLICATE_PRODUCT_FORMAT_NAME ||
                errorMessage.PRODUCT_FORMAT_NAME_STRING_BASE ||
                errorMessage.PRODUCT_FORMAT_NAME_STRING_EMPTY ||
                errorMessage.PRODUCT_FORMAT_NAME_MAXIMUM_LENGTH ||
                errorMessage.PRODUCT_FORMAT_NAME_ANY_REQUIRED
              }
              required
            />
            <TextInput
              onChange={(event) => {
                setProductFormatNameEn(event.target.value)
                clearErrorMessage()
              }}
              value={productFormatNameEn}
              helperText={
                errorMessage.PRODUCT_FORMAT_NAME_EN ||
                errorMessage.IS_DUPLICATE_PRODUCT_FORMAT_NAME_EN ||
                errorMessage.PRODUCT_FORMAT_NAME_EN_STRING_BASE ||
                errorMessage.PRODUCT_FORMAT_NAME_EN_STRING_EMPTY ||
                errorMessage.PRODUCT_FORMAT_NAME_EN_MAXIMUM_LENGTH ||
                errorMessage.PRODUCT_FORMAT_NAME_EN_ANY_REQUIRED
              }
              label={t('MEDICINE_FORMAT.INPUT.PRODUCT_FORMAT_NAME_EN')}
            />
          </Box>
        </PageModal>
      </Box>
    </Box>
  )
}
